import { supabase } from './supabase'
import { api, adminApi } from './api'

// Local knowledge base content — used as an offline fallback so the Support
// center is always fully navigable, even before the backend is configured.
export const KB_CATEGORIES = [
  { slug: 'getting-started', name: 'Getting Started', description: 'Onboarding, setup and first steps' },
  { slug: 'billing', name: 'Billing & Payments', description: 'Invoices, subscriptions and payment methods' },
  { slug: 'products', name: 'Products & Features', description: 'SQUAMED, SQUADENT, LITE and NAMMAWARA features' },
  { slug: 'account', name: 'Accounts & Security', description: 'Passwords, logins and account settings' },
  { slug: 'technical', name: 'Technical Support', description: 'Errors, integrations and API questions' },
]

export const KB_ARTICLES = [
  {
    slug: 'welcome-to-opraiz',
    category: 'getting-started',
    title: 'Welcome to OPRAIZ',
    summary: 'Everything you need to get started with your OPRAIZ workspace.',
    body: `Your OPRAIZ workspace brings hospital, dental and government health systems together on one platform. Start by:

1. Completing your organisation profile in **Account**.
2. Launching the **interactive demo** for your product to explore every module.
3. Choosing a **plan** from the Pricing page and completing checkout.

Need a guided walkthrough? Our team is available 7 days a week.`,
  },
  {
    slug: 'manage-subscriptions',
    category: 'billing',
    title: 'How do I switch or cancel my subscription?',
    summary: 'Upgrade, downgrade, pause or cancel directly from your Account page.',
    body: `Go to **Account → Subscriptions** and choose **Manage** on the plan you want to change. You can:

- **Upgrade or downgrade** to any plan for the same product. The change applies at the next billing cycle.
- **Pause** a subscription if you need a break.
- **Cancel** at any time — no long-term lock-in. Access continues until the end of the current billing period.

Invoices are available under **Account → Invoices**.`,
  },
  {
    slug: 'product-demos',
    category: 'products',
    title: 'What is included in an interactive demo?',
    summary: 'Every product ships with a fully working, self-contained demo.',
    body: `Each product page includes a **View Live Demo** button. The demo runs fully in your browser with realistic sample data:

- Registered patients, billing, scheduling, inventory and lab workflows.
- The **ORBO DIYA** AI assistant powered by Baanzon AI, answering with live demo data.
- Sample data persists locally in your browser and can be reset at any time.

Demos don't require a subscription and are free to explore.`,
  },
  {
    slug: 'reset-password',
    category: 'account',
    title: 'How do I reset my password?',
    summary: 'Forgot your password? Reset it in a few clicks.',
    body: `1. Visit **Forgot password** from the login page.
2. Enter your account email.
3. Follow the reset link we send you.

If you don't receive the email within a few minutes, check your spam folder and then open a support ticket.`,
  },
  {
    slug: 'checkout-errors',
    category: 'technical',
    title: 'I see an error during checkout. What should I do?',
    summary: 'Troubleshooting failed payments and checkout errors.',
    body: `Most checkout errors are transient. First try:

- Refreshing the page and trying again.
- Using a different payment method (UPI, netbanking, card).
- Checking that your payment mode has sufficient balance/limit.

If payments keep failing, raise a ticket with your **plan name**, **payment provider** and any reference numbers shown, and we'll investigate the same day.`,
  },
]

export const SLA_LABELS = {
  low: '72h',
  normal: '24h',
  high: '8h',
  urgent: '4h',
}

// Offline/error fallback contact — surfaced in the Support center whenever live
// ticketing is temporarily unavailable so visitors always have a reach-out path.
export const SUPPORT_EMAIL = 'sales@opraiz.com'
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=Support%20request`

const isMarkdownHeader = (line) => line.startsWith('#')

function markdownToHtml(md) {
  const lines = md.split('\n')
  const out = []
  let inList = false
  const flushList = () => {
    if (inList) {
      out.push('</ul>')
      inList = false
    }
  }
  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) {
      flushList()
      out.push('<br/>')
      continue
    }
    if (isMarkdownHeader(line)) {
      flushList()
      const level = line.match(/^#+/)[0].length
      out.push(`<h${level}>${line.replace(/^#+\s*/, '')}</h${level}>`)
      continue
    }
    if (line.startsWith('- ')) {
      if (!inList) {
        out.push('<ul>')
        inList = true
      }
      out.push(`<li>${line.replace(/^- /, '')}</li>`)
      continue
    }
    flushList()
    out.push(`<p>${line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</p>`)
  }
  flushList()
  return out.join('')
}

// Renders a KB article body (markdown-lite) safely.
export function articleHtml(article) {
  return markdownToHtml(article?.body || '')
}

export async function fetchKB() {
  if (!supabase) {
    console.warn('[support] no backend configured — serving local KB')
    return { categories: KB_CATEGORIES, articles: KB_ARTICLES, local: true }
  }
  const { data, error } = await supabase
    .from('kb_articles')
    .select('*, support_categories(id, name, slug, description)')
    .eq('published', true)
    .order('created_at', { ascending: true })
  if (error || !data || data.length === 0) {
    if (error) console.warn('[support] fetchKB error, falling back to local KB:', error)
    else console.warn('[support] backend returned no KB articles — serving local KB')
    return { categories: KB_CATEGORIES, articles: KB_ARTICLES, local: true }
  }
  const arts = data.map((a) => ({
    ...a,
    category: a.support_categories?.slug || 'other',
  }))
  // Categories must come from the DB (they carry real ids that tickets need).
  // Dedupe by slug; only fall back to the local manifest if the DB returned none.
  const dbCats = arts
    .map((a) => a.support_categories)
    .filter((c) => c && c.id && c.slug)
  const cats = dbCats.length
    ? [...new Map(dbCats.map((c) => [c.slug, c])).values()].map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
        description: c.description,
      }))
    : KB_CATEGORIES
  return { categories: cats, articles: arts, local: false }
}

export async function fetchMyTickets() {
  if (!supabase) {
    console.warn('[support] fetchMyTickets called with no backend configured')
    throw new Error(`Live support is temporarily unavailable. Please email ${SUPPORT_EMAIL} and we will help right away.`)
  }
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*, support_messages(*)')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data || []
}

export function createSupportTicket(payload) {
  return api('support-create-ticket', payload)
}

// Ticket attachments
export const createTicketAttachment = (payload) => api('ticket-attachment', { action: 'create', ...payload })
export const listTicketAttachments = (ticketId) => api('ticket-attachment', { action: 'list', ticket_id: ticketId })
export const getTicketAttachmentUrl = (ticketId, id) => api('ticket-attachment', { action: 'url', ticket_id: ticketId, id })

// Admin-console scoped attachment helpers (carry the admin session's JWT).
export const adminListTicketAttachments = (ticketId) => adminApi('ticket-attachment', { action: 'list', ticket_id: ticketId })
export const adminGetTicketAttachmentUrl = (ticketId, id) => adminApi('ticket-attachment', { action: 'url', ticket_id: ticketId, id })

export function replyToSupportTicket(payload) {
  return api('support-reply', payload)
}

export async function runSlaScan() {
  return adminApi('support-notify', {})
}

import { isSupport } from './roles'

const ADMIN_EMAILS = (import.meta.env.VITE_ADMIN_EMAILS || '')
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean)

// Server-managed role (app_metadata) is the source of truth; the email
// allowlist remains as an operational fallback for pre-migration accounts.
export function isSupportAdmin(user) {
  if (!user) return false
  if (isSupport(user)) return true
  return ADMIN_EMAILS.includes((user.email || '').toLowerCase())
}

export function adminSupport(action, payload = {}) {
  return adminApi('support-admin', { action, ...payload })
}